import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import API_CONFIG from '../../apiConfig';
import ImageUpload from './ImageUpload';
import SettingsPanel from './SettingsPanel';
import { examplePresets, ExamplePreset } from './examplePresets';
import { useCreditEstimation } from '../../hooks/useCreditEstimation';
import { useModeRestrictions } from '../../hooks/useModeRestrictions';
import CreditCostPreview from '../ui/CreditCostPreview';
import InsufficientCreditsModal from '../ui/InsufficientCreditsModal';

interface LocationState {
  inputImages: string[];
  prompt: string;
  style: string;
  variations: number;
  recreatingFrom: string;
  generatedImages?: string[];
}

const FashionExtractor: React.FC = () => {
  const { currentUser, userCredits, refreshUserCredits } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  // State to track if an image is uploaded
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [extractedClothes, setExtractedClothes] = useState<string[]>(state?.generatedImages || []);
  const [prompt, setPrompt] = useState(state?.prompt || '');
  const [selectedPreset, setSelectedPreset] = useState<number | null>(null);
  const [isExample, setIsExample] = useState(false);
  const [extractionType, setExtractionType] = useState(state?.style || 'full-outfit');
  const [mode, setMode] = useState<string>('performance'); // Default to performance (available to all)
  const [errorMessage, setErrorMessage] = useState('');
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  // Credit estimation hook
  const [creditEstimation, estimateCreditCost] = useCreditEstimation('fashion-extractor');

  // Mode restrictions hook
  const modeRestrictions = useModeRestrictions();

  // Effect to handle recreation state
  useEffect(() => {
    const loadInputImages = async () => {
      if (state?.inputImages && state.inputImages.length > 0) {
        setImageUrls(state.inputImages);
        if (state.generatedImages) {
          setExtractedClothes(state.generatedImages);
          setIsExample(false);
        }
      }
    };

    loadInputImages();
  }, [state]);

  // Clear error messages when user uploads new images
  useEffect(() => {
    if (imageUrls.length > 0) {
      setErrorMessage('');
    }
  }, [imageUrls]);

  // Handle mode restrictions - automatically switch to available mode if current is restricted
  useEffect(() => {
    if (modeRestrictions.hasInitialLoad && !modeRestrictions.canUseMode(mode)) {
      // Switch to the first available mode (Performance is always available)
      const availableMode = modeRestrictions.availableModes.find(m => !m.disabled);
      if (availableMode) {
        setMode(availableMode.value);
      }
    }
  }, [modeRestrictions.hasInitialLoad, modeRestrictions.availableModes, mode, modeRestrictions.canUseMode]);

  // Credit estimation effect - trigger when generation settings change
  useEffect(() => {
    // Only estimate if we have required data and user is authenticated
    if (!currentUser?.uid || imageUrls.length === 0) {
      return;
    }

    // Build the same payload structure as the actual generation
    const estimationPayload = {
      extractionType: extractionType,
      inputImageUrls: imageUrls,
      prompt: prompt,
      numImages: 1,
      mode: mode.toLowerCase(),
      quality: "high",
      preserveDetails: true,
      userId: currentUser.uid
    };

    estimateCreditCost(estimationPayload);
  }, [
    currentUser?.uid,
    imageUrls,
    estimateCreditCost
  ]);

  // Example handling
  const handlePresetSelect = (presetId: number) => {
    const preset = examplePresets.find((p: ExamplePreset) => p.id === presetId);
    if (preset) {
      setPrompt(preset.prompt);
      setSelectedPreset(presetId);
      setExtractedClothes(preset.results);
      setIsExample(true);
      // Set the example image as the current input image
      setImageUrls([preset.preview]);
    }
  };

  const handleDownloadImage = async (imageUrl: string, index: number) => {
    const filename = `${isExample ? 'example' : 'extracted'}-fashion-item-${index + 1}.jpg`;
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download image:', error);
      window.open(imageUrl, '_blank');
    }
  };

  const handleExtractFashion = async () => {
    if (imageUrls.length === 0) return;

    // Check if user can afford the generation
    if (!creditEstimation.canAfford) {
      setShowInsufficientCreditsModal(true);
      return;
    }

    setExtracting(true);
    setIsExample(false);
    setErrorMessage(''); // Clear any existing error messages
    try {
      const payload = {
        extractionType: extractionType,
        inputImageUrls: imageUrls,
        prompt: prompt,
        numImages: 1,
        userId: currentUser?.uid || 'anonymous',
        mode: mode.toLowerCase(),
        quality: "high",
        preserveDetails: true,
        removeBackground: true
      };

      const response = await fetch(API_CONFIG.FASHION_EXTRACTOR_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      // Check HTTP status
      if (!response.ok) {
        setErrorMessage('Something went wrong. Please try again.');
        return;
      }

      const data = await response.json();

      // Check if images were returned
      if (data.success && Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
        setExtractedClothes(data.imageUrls);
        // Refresh user credits after successful generation
        await refreshUserCredits();
        setErrorMessage('');
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Failed to extract fashion:', error);
      setErrorMessage('Failed to extract fashion items. Please try again.');
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="w-full relative">
      {/* Main Content Area - Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 p-2">
        {/* First Column - Image Upload */}
        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Fashion Image</h2>
            <span className="text-sm text-text-description">Upload a fashion image to extract clothing items</span>
          </div>

          <div className="p-4 flex flex-col min-h-[400px]">
            <ImageUpload
              imageUrls={imageUrls}
              setImageUrls={setImageUrls}
              isExample={isExample}
              onExampleSelect={handlePresetSelect}
            />
          </div>
        </div>

        {/* Second Column - Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Controls</h2>
            <span className="text-sm text-text-description">Configure your fashion extraction settings</span>
          </div>

          <div className="p-4 space-y-3 flex-1 overflow-y-auto">
            {/* Clear Button Section */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-text-heading">Outfit Extractor (Flatlay)</label>
                {/* Clear Button */}
                <button
                  onClick={() => {
                    // Clear selected preset
                    setSelectedPreset(null);
                    // Clear prompt and reset settings (keep extraction type as full-outfit)
                    setPrompt('');
                    setMode('performance');
                    // Clear generated images and examples
                    setExtractedClothes([]);
                    setIsExample(false);
                    setErrorMessage('');
                    // Clear uploaded images
                    setImageUrls([]);
                    // Reset advanced settings
                    setShowAdvancedSettings(false);
                  }}
                  className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded-md transition-colors"
                  title="Clear all settings, images, and results"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Settings Panel */}
            <div className="bg-accent-sand rounded-lg p-3">
              <SettingsPanel
                prompt={prompt}
                setPrompt={setPrompt}
                extractionType={extractionType}
                setExtractionType={setExtractionType}
                mode={mode}
                setMode={setMode}
                modeRestrictions={modeRestrictions}
                showAdvancedSettings={showAdvancedSettings}
                setShowAdvancedSettings={setShowAdvancedSettings}
              />
            </div>

            {/* Credit Cost Preview */}
            <div className="bg-accent-sand rounded-lg p-2">
              <CreditCostPreview
                creditCost={creditEstimation.creditCost}
                isEstimating={creditEstimation.isEstimating}
                error={creditEstimation.error}
                canAfford={creditEstimation.canAfford}
                className="text-xs"
              />
            </div>

            {/* Extract button */}
            <button
              className={`w-full px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${imageUrls.length > 0
                ? 'bg-secondary-500 hover:bg-secondary-600 text-white cursor-pointer'
                : 'bg-gray-300 text-text-description cursor-not-allowed'
                }`}
              onClick={handleExtractFashion}
              disabled={imageUrls.length === 0 || extracting}
            >
              {extracting ? (
                <div className="flex items-center justify-center space-x-2">
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <span>Extracting...</span>
                </div>
              ) : (
                <>
                  <i className="fas fa-cut mr-2"></i>
                  Extract Fashion
                </>
              )}
            </button>
            {imageUrls.length === 0 && (
              <p className="text-xs text-text-description text-center">Upload an image to enable extraction</p>
            )}

            {/* Error message display */}
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs text-red-700">{errorMessage}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Third Column - Generated Result */}
        <div className="bg-white rounded-lg shadow-sm border border-accent-taupe flex flex-col">
          <div className="p-4 border-b border-accent-taupe flex-shrink-0">
            <h2 className="text-lg font-semibold text-text-heading">Extracted Results</h2>
            <span className="text-sm text-text-description">
              {isExample ? 'Example results will appear here' : 'Extracted fashion items will appear here'}
            </span>
          </div>

          <div className="p-4 flex-1 flex flex-col">
            {extracting ? (
              <div className="flex-1 bg-accent-sand rounded-lg border border-accent-taupe flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary-500 mx-auto mb-4"></div>
                  <p className="text-sm text-text-description">Extracting fashion items...</p>
                  <p className="text-xs text-text-description mt-2">This may take a few moments</p>
                </div>
              </div>
            ) : extractedClothes.length > 0 ? (
              <div className="relative flex-1 min-h-[400px]">
                <div
                  className={`${
                    extractedClothes.length === 1 ? 'grid grid-cols-1' : 'grid grid-cols-1 sm:grid-cols-2'
                  } gap-2 max-h-[480px] overflow-y-auto`}
                >
                  {extractedClothes.map((imageUrl, index) => (
                    <div
                      key={`${imageUrl}-${index}`}
                      className="relative flex flex-col h-72 bg-gradient-to-br from-white via-gray-50 to-gray-100 rounded-xl shadow border border-accent-taupe overflow-hidden"
                    >
                      <div className="absolute top-2 left-2 z-10 bg-secondary-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full shadow">
                        #{index + 1}
                      </div>
                      <div className="flex-1 flex items-center justify-center bg-accent-sand min-h-0 p-2">
                        <img
                          src={imageUrl}
                          alt={`${isExample ? 'Example' : 'Extracted'} fashion item ${index + 1}`}
                          className="max-h-full max-w-full object-contain rounded-lg bg-white"
                        />
                      </div>
                      <div className="flex justify-center items-center gap-2 px-3 py-2 bg-white border-t border-accent-taupe">
                        <button
                          type="button"
                          className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium"
                          onClick={() => handleDownloadImage(imageUrl, index)}
                        >
                          Download
                        </button>
                        <button
                          type="button"
                          className="text-text-description hover:text-secondary-600 hover:bg-secondary-100 border border-accent-taupe hover:border-secondary-300 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium"
                          onClick={() => {
                            navigate('/canvas-editor', {
                              state: {
                                inputImages: [imageUrl],
                                prompt: '',
                                style: '',
                                variations: 1,
                                recreatingFrom: '',
                                tool: 'upscaler',
                              },
                            });
                          }}
                        >
                          Upscale
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Example notice */}
                {isExample && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start">
                      <i className="fas fa-info-circle text-blue-600 mr-2 mt-0.5"></i>
                      <div>
                        <p className="text-xs text-blue-800 font-medium">Example Results</p>
                        <p className="text-xs text-blue-700 mt-1">
                          These are example extracted fashion items. Upload your own fashion model image to extract real clothing items.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 bg-accent-sand rounded-lg border border-accent-taupe flex items-center justify-center min-h-[400px]">
                <div className="text-center text-text-description">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">Upload a fashion image and click Extract to see results</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Insufficient Credits Modal */}
      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        requiredCredits={creditEstimation.creditCost || 0}
        availableCredits={userCredits}
        featureName="Outfit Extractor (Flatlay)"
      />
    </div>
  );
};

export default FashionExtractor; 